import { CreateReportItemRequest, CreateReportItemsFromSelectionsRequest, ReorderReportItemsReply, ReorderReportItemsRequest, ReportItemReply } from '@/backend/express/controllers/reportItemController';
import { CreateReportProcedureInstance, ReportItemInterface, ReportItemTreeObject, ReportParams, ReportProcedureObject, ReportType } from '@/types/reportTypes';
import { create } from 'zustand';
import { ReportItemController } from '../controllers/reportItemController';
import { Report } from '@/backend/models/report';
import { ReportController } from '../controllers/reportController';
import { ContextLog } from '@/backend/models/contextLog';
import { GenerateReportFromTemplateRequest, ReportReply, ReportsReply } from '@/backend/express/controllers/reportController';
import { useParameterStore } from './ParameterStore';
import { ContextLogReadAllResponse } from '@/types/contextLogTypes';
import { ReportEquipment } from '@/backend/models/reportEquipment';
import { ReportEquipmentParams } from '@/types/reportEquipment';
import { ReportEquipmentController } from '../controllers/reportEquipmentController';
import { Part } from '@/backend/models/part';
import { ReportDeviceUnderTestFrontendController } from '../controllers/reportDeviceUnderTestController';
import { RunReportFrontendController } from '../controllers/runReportFrontendController';
import { CodedReportTemplateResponse, RunReportListItem } from '@/types/runReportTypes';
import { RunSnapshot } from '@/reports/runs';
import { RunReportContext } from '@/reports/types';

export type ReportStore = {
  reports: Report[];
  /** the coded test reports this org holds, listed with the legacy reports */
  runReports: RunReportListItem[];
  /** the open coded template's runs; null until read */
  codedTemplate: Omit<CodedReportTemplateResponse, 'status' | 'template'> | null;
  /** the procedure template behind the open coded template, read like a run, for its binding check; undefined until fetched */
  codedTemplateProcedure: RunSnapshot | null | undefined;
  fetchCodedTemplateProcedure: (reportId: string) => Promise<void>;
  /** one of the template's runs, read as its reports would read it: what the template page previews against */
  fetchCodedTemplatePreview: (reportId: string, runId: string) => Promise<RunReportContext | null>;
  templateReports: Report[];
  testsForTemplate: Report[];
  currentReport: Report | null;
  reportItems: ReportItemInterface[];
  procedures: ReportProcedureObject[];
  devicesUnderTest: Part[];
  reportEquipment: ReportEquipment[];
  rootReportItems: ReportItemTreeObject[];
  contextLogs: ContextLog[];
  reset: () => void;
  listReports: (type: ReportType) => Promise<void>;
  listRunReports: () => Promise<void>;
  createReport: (title: string, type: ReportType) => Promise<ReportReply>;
  /** with a report key, a coded report template on the one procedure template given */
  /** `replaceLegacy`: a coded template on a draft version, in place of the legacy ones it carries */
  createReportWithProcedureInstances: (title: string, type: ReportType, procedureInstances: CreateReportProcedureInstance[], reportKey?: string | null, options?: { replaceLegacy?: boolean }) => Promise<ReportReply>;
  fetchCodedTemplate: (reportId: string) => Promise<void>;
  fetchReport: (id: string) => Promise<void>;
  readReport: (id: string) => Promise<void>;
  updateReport: (report: ReportParams) => Promise<ReportReply>;
  deleteReport: (id: string) => Promise<void>;
  listReportItems: (reportId: string) => Promise<void>;
  addReportItem: (request: CreateReportItemRequest) => Promise<ReportItemReply>;
  updateReportItem: (reportItem: ReportItemInterface) => Promise<void>;
  fetchReportItem: (reportItemId: string) => Promise<void>;
  deleteReportItem: (id: string) => Promise<void>;
  reorderReportItems: (request: ReorderReportItemsRequest) => Promise<ReorderReportItemsReply>;
  readReportProcedures: (reportId: string) => Promise<void>;
  addProcedureToReport: (procedureId: string, reportId: string) => Promise<void>;
  removeProcedureFromReport: (procedure: ReportProcedureObject, reportId: string) => Promise<void>;
  importReportItemsForProcedure: (procedureId: string, reportId: string) => Promise<void>;
  importDeviceUnderTestForProcedure: (procedureId: string, reportId: string) => Promise<void>;
  readReportDevicesUnderTest: (reportId: string) => Promise<void>;
  removeDeviceUnderTestFromReport: (reportId: string, partId: string) => Promise<void>;
  readEquipmentForReport: (reportId: string) => Promise<void>;
  importEquipmentForProcedure: (procedureId: string, reportId: string) => Promise<void>;
  updateEquipment: (equipment: ReportEquipmentParams) => Promise<void>;
  removeEquipmentFromReport: (reportEquipmentId: string) => Promise<void>;
  setReportItems: (reportItems: ReportItemInterface[]) => void;
  readReportsBySourceId: (sourceId: string) => Promise<ReportsReply>;
  fetchReportsUsingProcedure: (procedureId: string) => Promise<void>;
  fetchTestsForTemplate: (reportTemplateId: string) => Promise<void>;
  fetchContextLogsForReport: (reportId: string) => Promise<ContextLogReadAllResponse>;
  fetchContextLogsAndParametersForReport: (reportId: string) => Promise<void>;
  exportReportJson: (reportId: string, reportTitle: string) => Promise<void>;
  generateReport: (request: GenerateReportFromTemplateRequest) => Promise<ReportReply>;
  createReportItemsFromSelectedItems: (request: CreateReportItemsFromSelectionsRequest) => Promise<void>;
};

const intialState = {
  reports: [],
  runReports: [],
  codedTemplate: null,
  codedTemplateProcedure: undefined,
  templateReports: [],
  testsForTemplate: [],
  currentReport: null,
  reportItems: [],
  procedures: [],
  devicesUnderTest: [],
  reportEquipment: [],
  rootReportItems: [],
  contextLogs: [],
};

export const useReportStore = create<ReportStore>((set, get) => ({
  ...intialState,
  reset: () => {
    set(intialState);
  },
  listReports: async (type: ReportType) => {
    const response = await new ReportController().readAllReports(type);
    if (!response.errors) {
      const reports = response.reports.map(report => new Report(report));
      if (type === ReportType.Template) {
        set({ templateReports: reports });
      } else {
        set({ reports });
      }
    }
  },
  listRunReports: async () => {
    const response = await new RunReportFrontendController().list();
    if (response.status === 200) {
      set({ runReports: response.runReports });
    }
  },
  fetchCodedTemplate: async (reportId) => {
    const response = await new ReportController().readCodedReportTemplate(reportId);
    if (response.status === 200) {
      set({
        codedTemplate: {
          runs: response.runs,
          canEdit: response.canEdit ?? false,
        },
      });
    }
  },
  fetchCodedTemplateProcedure: async (reportId) => {
    const response = await new ReportController().readCodedTemplateProcedure(reportId);
    set({ codedTemplateProcedure: response.status === 200 ? response.procedure : null });
  },
  fetchCodedTemplatePreview: async (reportId, runId) => {
    const response = await new ReportController().readCodedTemplatePreview(reportId, runId);
    return response.status === 200 && response.context ? response.context : null;
  },
  createReport: async (title, type) => {
    const response = await new ReportController().createReport({ title, type });
    if (!response.errors && response.report) {
      const { reports, templateReports } = get();
      if (type === ReportType.Template) {
        set({ templateReports: [...templateReports, new Report(response.report)] });
      } else {
        set({ reports: [...reports, new Report(response.report)] });
      }
    }
    return response;
  },
  createReportWithProcedureInstances: async (title, type, procedureInstances, reportKey, options) => {
    const controller = new ReportController();
    const response = await controller.createReportWithProcedureInstances({ title, type, procedureInstances, ...(reportKey ? { reportKey } : {}), ...(reportKey && options?.replaceLegacy ? { replaceLegacy: true } : {}) });
    if (!response.errors && response.report) {
      const { reports } = get();
      reports.push(new Report(response.report));
      set({ reports: [...reports] });
    }
    return response;
  },
  fetchReport: async (id) => {
    await Promise.all([
      get().readReport(id),
      get().listReportItems(id)
    ]);
  },
  readReport: async (id) => {
    const response = await new ReportController().readReport(id);
    if (!response.errors && response.report) {
      set({ currentReport: new Report(response.report) });
    }
  },
  updateReport: async (report) => {
    const response = await new ReportController().updateReport({ report });
    if (!response.errors && response.report) {
      set({ currentReport: new Report(response.report) });
    }
    return response;
  },
  deleteReport: async (id) => {
    const response = await new ReportController().deleteReport(id);
    if (!response.errors) {
      const reports = get().reports.filter((report) => report.id !== id);
      const templates = get().templateReports.filter((template) => template.id !== id);
      set({ reports, templateReports: templates });
    }
  },
  listReportItems: async (reportId) => {
    const response = await new ReportItemController().readAllReportItems(reportId);
    if (!response.errors) {
      get().setReportItems(response.reportItems);
    }
  },
  setReportItems(reportItems) {
    set({ reportItems, rootReportItems: generateReportTree(reportItems)});
  },
  addReportItem: async (request) => {
    const response = await new ReportItemController().createReportItem(request);
    void get().listReportItems(request.reportId);
    return response;
  },
  updateReportItem: async (reportItem) => {
    const response = await  new ReportItemController().updateReportItem({ reportItem });
    if (!response.errors) {
      const newReportItems = get().reportItems.map(item => (item.id === reportItem.id ? response.reportItem : item));
      get().setReportItems(newReportItems);
    }
  },
  fetchReportItem: async (id) => {
    const response = await new ReportItemController().readReportItem(id);
    if (!response.errors && response.reportItem) {
      const newReportItems = get().reportItems.map(item => (item.id === id ? response.reportItem : item));
      get().setReportItems(newReportItems);
    }
  },
  deleteReportItem: async (id) => {
    const response = await new ReportItemController().deleteReportItem(id).then(() => get().listReportItems(get().currentReport?.id as string));
    return response;
  },
  reorderReportItems: async (request) => {
    const response = await  new ReportItemController().reorderReportItems(request);
    if (response.status === 200) {
      void get().listReportItems(get().currentReport?.id as string);
    }
    return response;
  },
  readReportProcedures: async (reportId) => {
    const response = await new ReportController().readReportProcedures(reportId);
    if (!response.errors) {
      set({ procedures: response.procedures });
    }
  },
  addProcedureToReport: async (procedureId, reportId) => {
    const response = await new ReportController().addProcedureToReport({ reportId, procedureId });
    if (!response.errors && response.procedure) {
      const { procedures } = get();
      set({ procedures: [...procedures, response.procedure] });
    }
  },
  removeProcedureFromReport: async (procedure, reportId) => {
    const { procedures } = get();
    const response = await new ReportController().removeProcedureFromReport({ reportId, procedureId: procedure.item.id, procedureType: procedure.item.procedureType });
    if (response.status === 200) {
      set({ procedures: procedures.filter(({ item }) => item.id !== procedure.item.id) });
      void get().listReportItems(reportId);
    }
  },
  importReportItemsForProcedure: async (procedureId, reportId) => {
    const response = await new ReportItemController().importForProcedure({ procedureId, reportId });
    if (!response.errors) {
      void get().listReportItems(reportId);
    }
  },
  importDeviceUnderTestForProcedure: async (procedureId, reportId) => {
    const response = await new ReportDeviceUnderTestFrontendController().importDeviceUnderTestForProcedure(procedureId, reportId);
    if (!response.errors) {
      await get().readReportDevicesUnderTest(reportId);
    }
  },
  readReportDevicesUnderTest: async (reportId) => {
    const response = await new ReportDeviceUnderTestFrontendController().getReportDevicesUnderTest(reportId);
    if (!response.errors) {
      set({ devicesUnderTest: response.parts });
    }
  },
  removeDeviceUnderTestFromReport: async (reportId, partId) => {
    const response = await new ReportDeviceUnderTestFrontendController().removeDeviceUnderTestFromReport(reportId, partId);
    if (!response.errors) {
      void get().readReportDevicesUnderTest(reportId);
    }
  },
  readEquipmentForReport: async (reportId) => {
    await new ReportEquipmentController().readAllReportEquipmentForReport(reportId).then((res) => {
      if (!res.errors && res.reportEquipment) {
        set({ reportEquipment: res.reportEquipment.map((equipment) => new ReportEquipment(equipment)) });
      }
    });
  },
  importEquipmentForProcedure: async (procedureId, reportId) => {
    await new ReportEquipmentController().importEquipmentFromProcedure(procedureId, reportId).then((res) => {
      if (!res.errors && res.reportEquipment) {
        const { reportEquipment } = get();
        const newEquipment = res.reportEquipment.map((equipment) => new ReportEquipment(equipment));
        set({ reportEquipment: [...reportEquipment, ...newEquipment] });
      }
    });
  },
  updateEquipment: async (equipment) => {
    await new ReportEquipmentController().updateReportEquipment(equipment).then((res) => {
      if (!res.errors && res.status === 200) {
        const { reportEquipment } = get();
        const newEquipment = new ReportEquipment(res.reportEquipment);
        set({ reportEquipment: reportEquipment.map((e) => (e.id === newEquipment.id ? newEquipment : e)) });
      }
    });
  },
  removeEquipmentFromReport: async (reportEquipmentId) => {
    await new ReportEquipmentController().deleteReportEquipment(reportEquipmentId).then((res) => {
      if (!res.errors) {
        const { reportEquipment } = get();
        set({ reportEquipment: reportEquipment.filter((equipment) => equipment.id !== reportEquipmentId) });
      }
    });
  },
  readReportsBySourceId: async (sourceId) => {
    const response = await new ReportController().readReportsBySourceId(sourceId);
    return response;
  },
  fetchReportsUsingProcedure: async (procedureId) => {
    const controller = new ReportController();
    const response = await controller.readReportsForProcedure(procedureId);
    const reports = response.reports.filter((report) => report.type === ReportType.Report);
    const templateReports = response.reports.filter((report) => report.type === ReportType.Template);
    set({
      reports: reports.map((report) => new Report(report)),
      templateReports: templateReports.map((report) => new Report(report))
     });
  },
  fetchTestsForTemplate: async (reportTemplateId) => {
    const response = await new ReportController().readAllTestsForTemplate(reportTemplateId);
    if (!response.errors) {
      set({ testsForTemplate: response.reports.map((report) => new Report(report)) });
    }
  },
  fetchContextLogsForReport: async (reportId) => {
    const response = await new ReportController().readAllContextLogsForReport(reportId);
    if (!response.errors) {
      set({ contextLogs: response.contextLogs.map((contextLog) => new ContextLog(contextLog)) });
    }
    return response;
  },
  exportReportJson: async (reportId, reportTitle) => {
    const response = await new ReportController().exportJson(reportId);
    if (response.errors || response.status !== 200) return;

    const blob = new Blob([response.json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportTitle}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },
  fetchContextLogsAndParametersForReport: async (reportId: string) => {
    if (!reportId) return;

    await get().fetchContextLogsForReport(reportId);
    const contextLogIds = get().contextLogs.map(contextLog => contextLog.id);
    await useParameterStore.getState().fetchContextLogParametersForContextLogs(contextLogIds);
    const sourceIds = get().reportItems.map(reportItem => reportItem.sourceId).filter(sourceId => !!sourceId);
    await useParameterStore.getState().fetchParameters(sourceIds);
  },
  generateReport: async (request) => {
    return await new ReportController().generateReportFromTemplate(request);
  },
  createReportItemsFromSelectedItems: async (request) => {
    const response = await new ReportItemController().createFromSelectedItems(request);
    if (!response.errors) {
      await get().listReportItems(request.reportId);
    }
  },
}));


export type ReportLookup<T> = { [key: string]: T };

export function generateReportTree(reportItems: ReportItemInterface[]) {
  const reportItemLookup: ReportLookup<ReportItemTreeObject> = {};

  reportItems.forEach((reportItem) => {
    reportItemLookup[reportItem.id] = { ...reportItem, reportItems: [], wbs: ''};
  });

  const rootReportItems: ReportItemTreeObject[] = [];

  reportItems.forEach((reportItem) => {
    const currentReportItemNode = reportItemLookup[reportItem.id];
    if (!currentReportItemNode) return;

    if (currentReportItemNode.parentId === currentReportItemNode.reportId) {
      rootReportItems.push(currentReportItemNode);
    } else if (currentReportItemNode.parentId in reportItemLookup) {
      const parentReportItem = reportItemLookup[currentReportItemNode.parentId];
      parentReportItem.reportItems.push(currentReportItemNode);
    }
  });

  setWBS(rootReportItems, '');

  return rootReportItems;
}

function setWBS(nodes: ReportItemTreeObject[], parentWBS?: string) {
  nodes.forEach((node, index) => {
    const wbs = parentWBS ? `${parentWBS}.${index + 1}` : `${index + 1}`;
    node.wbs = wbs;
    if (node.reportItems.length) {
      setWBS(node.reportItems, wbs);
    }
  });
}

